import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { AlertTriangle, Loader2, Flag } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ReportUserModal = ({ open, onOpenChange, reportedUser, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      toast.error("Please select a reason for the report");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/reports/user`, {
        reported_user_id: reportedUser.id,
        reported_username: reportedUser.username,
        reason,
        details: details.trim() || null,
      });
      
      toast.success("Report submitted successfully. Our team will review it.");
      onOpenChange(false);
      setReason("");
      setDetails("");
      if (onSuccess) onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit report";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onOpenChange(false);
      setReason("");
      setDetails("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-destructive/20 rounded-full flex items-center justify-center mb-2">
            <Flag className="w-6 h-6 text-destructive" />
          </div>
          <DialogTitle>Report User</DialogTitle>
          <DialogDescription>
            Report <span className="font-semibold text-foreground">{reportedUser?.username}</span> for inappropriate behavior
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <Label>Reason for report *</Label>
            <RadioGroup value={reason} onValueChange={setReason}>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer">
                <RadioGroupItem value="insulting_username" id="insulting_username" />
                <Label htmlFor="insulting_username" className="flex-1 cursor-pointer">
                  <div className="font-medium">Insulting Username</div>
                  <div className="text-xs text-muted-foreground">Username contains offensive or inappropriate content</div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer">
                <RadioGroupItem value="faking_kills" id="faking_kills" />
                <Label htmlFor="faking_kills" className="flex-1 cursor-pointer">
                  <div className="font-medium">Faking Kills</div>
                  <div className="text-xs text-muted-foreground">User is marking kills that didn't happen</div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors cursor-pointer">
                <RadioGroupItem value="other" id="other" />
                <Label htmlFor="other" className="flex-1 cursor-pointer">
                  <div className="font-medium">Other</div>
                  <div className="text-xs text-muted-foreground">Other inappropriate behavior</div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">Additional details (optional)</Label>
            <Textarea
              id="details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional context that might help us investigate..."
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">{details.length}/500</p>
          </div>

          <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-muted-foreground">
              False reports may result in action against your account.
            </span>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="flex-1"
              disabled={submitting || !reason}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Flag className="w-4 h-4 mr-2" />
                  Submit Report
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportUserModal;
